import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { formatCurrency, formatDate } from '../../../utils/formatUtils';
import { DEBT_DETAIL_EXPORT_PREFIX } from '../constants/debtDetailConstants';
import logoAsset from '../../../assets/img/logo_constructora.png';
import { fetchDebtDetailBulk } from '../../../hooks/useDebt';

const DEFAULT_MARGIN_X = 14;
const DEFAULT_LINE_HEIGHT = 6;

const MONTH_NAMES = {
  1: 'Enero',
  2: 'Febrero',
  3: 'Marzo',
  4: 'Abril',
  5: 'Mayo',
  6: 'Junio',
  7: 'Julio',
  8: 'Agosto',
  9: 'Septiembre',
  10: 'Octubre',
  11: 'Noviembre',
  12: 'Diciembre',
};

const ADEQUACY_MONTHS = {
  2: 'Febrero',
  5: 'Mayo',
  8: 'Agosto',
  11: 'Noviembre',
};

export const sanitizeDebtDetailFilters = (filters = {}) => {
  if (!filters || typeof filters !== 'object') return {};
  const {
    page,
    per_page,
    contract_number: contractNumber,
    ...rest
  } = filters;

  const normalized = { ...rest };
  if (contractNumber) {
    normalized.contract_number = typeof contractNumber === 'string'
      ? contractNumber.trim()
      : contractNumber;
  }

  return normalized;
};

const sortDebtDetailData = (data = []) =>
  [...data].sort((a, b) => {
    const contractA = a?.header?.contract_number ?? '';
    const contractB = b?.header?.contract_number ?? '';
    return String(contractA).localeCompare(String(contractB), 'es', {
      numeric: true,
      sensitivity: 'base',
    });
  });

const mapSubscriber = (subscriber = {}, index = 0) => ({
  uuid: subscriber?.uuid ?? subscriber?.subscriber_uuid ?? null,
  name: subscriber?.name ?? subscriber?.full_name ?? subscriber?.subscriber ?? null,
  email: subscriber?.email ?? null,
  phone: subscriber?.phone ?? null,
  document: subscriber?.document ?? null,
  is_primary: index === 0 ? true : Boolean(subscriber?.is_primary),
});

const hasSubscriberData = (subscriber = {}) =>
  Boolean(
    subscriber &&
      (subscriber.name ||
        subscriber.email ||
        subscriber.phone ||
        subscriber.document ||
        subscriber.uuid)
  );

export const getHeaderSubscribers = (header = {}) => {
  if (Array.isArray(header.subscribers) && header.subscribers.length > 0) {
    return header.subscribers
      .map((subscriber, index) => mapSubscriber(subscriber, index))
      .filter(hasSubscriberData);
  }

  const fallback = mapSubscriber(
    {
      uuid: header.subscriber_uuid ?? null,
      name: header.subscriber ?? null,
      email: header.subscriber_email ?? null,
      phone: header.subscriber_phone ?? null,
      document: header.subscriber_document ?? null,
      is_primary: true,
    },
    0
  );

  return hasSubscriberData(fallback) ? [fallback] : [];
};

export const getPrimarySubscriber = (header = {}) => {
  const [primary] = getHeaderSubscribers(header);
  return (
    primary ||
    mapSubscriber(
      {
        uuid: header.subscriber_uuid ?? null,
        name: header.subscriber ?? null,
        email: header.subscriber_email ?? null,
        phone: header.subscriber_phone ?? null,
        document: header.subscriber_document ?? null,
        is_primary: true,
      },
      0
    )
  );
};

export const getSubscriberNameSummary = (header = {}) => {
  const subscribers = getHeaderSubscribers(header);
  if (subscribers.length === 0) {
    return header.subscriber || 'Sin suscriptor';
  }
  if (subscribers.length === 1) {
    return subscribers[0].name || header.subscriber || 'Sin suscriptor';
  }
  const [first, ...rest] = subscribers;
  return `${first.name || 'Sin suscriptor'} (+${rest.length} más)`;
};

export const getSubscriberEmailSummary = (header = {}, separator = ' | ') => {
  const subscribers = getHeaderSubscribers(header);
  const uniqueEmails = new Set(
    subscribers
      .map((subscriber) => subscriber.email)
      .filter((email) => typeof email === 'string' && email.trim().length > 0)
      .map((email) => email.trim())
  );

  if (uniqueEmails.size === 0 && header.subscriber_email) {
    uniqueEmails.add(header.subscriber_email);
  }

  if (uniqueEmails.size === 0) {
    return 'Sin email';
  }

  return Array.from(uniqueEmails).join(separator);
};

export const getSubscriberPhoneSummary = (header = {}, separator = ' - ') => {
  const subscribers = getHeaderSubscribers(header);
  const uniquePhones = new Set(
    subscribers
      .map((subscriber) => subscriber.phone)
      .filter((phone) => typeof phone === 'string' && phone.trim().length > 0)
      .map((phone) => phone.trim())
  );

  if (uniquePhones.size === 0 && header.subscriber_phone) {
    uniquePhones.add(header.subscriber_phone);
  }

  if (uniquePhones.size === 0) {
    return 'Sin teléfono';
  }

  return Array.from(uniquePhones).join(separator);
};

export const getSubscriberCompactSummary = (header = {}, separator = ' • ') => {
  const subscribers = getHeaderSubscribers(header);
  if (subscribers.length === 0) {
    const fallbackName = header.subscriber || 'Sin suscriptor';
    const fallbackEmail = header.subscriber_email;
    return fallbackEmail ? `${fallbackName} (${fallbackEmail})` : fallbackName;
  }

  return subscribers
    .map((subscriber) => {
      const name = subscriber.name || 'Sin suscriptor';
      return subscriber.email ? `${name} (${subscriber.email})` : name;
    })
    .join(separator);
};

export const buildRowKey = (header = {}) => {
  const contract = header.contract_uuid || header.contract_number || 'unknown-contract';
  return String(contract);
};

export const aggregateSelectedSummary = (selectedMap = {}) => {
  const entries = Object.values(selectedMap);

  const coupons = entries.flatMap((entry) => Object.values(entry.coupons || {}));
  const recipientKeys = new Set();

  entries.forEach((entry) => {
    const header = entry.header || {};
    const subscribers = getHeaderSubscribers(header);
    subscribers.forEach((subscriber) => {
      const normalizedKey = subscriber.uuid
        ? `uuid:${subscriber.uuid}`
        : subscriber.email
        ? `email:${subscriber.email.toLowerCase()}`
        : subscriber.name
        ? `name:${subscriber.name.toLowerCase()}`
        : null;

      if (normalizedKey) {
        recipientKeys.add(normalizedKey);
      }
    });
  });

  const totalAmount = coupons.reduce(
    (acc, coupon) => acc + Number(coupon.third_due_amount || coupon.regulariza1_amount || 0),
    0
  );

  return {
    selectedCoupons: coupons.length,
    selectedRecipients: recipientKeys.size,
    totalAmount,
  };
};

export const buildDebtReminderPayload = (
  selectedMap = {},
  { message, includeAmounts = false } = {}
) => {
  const emails_deudas = [];

  Object.values(selectedMap).forEach((entry) => {
    const header = entry.header || {};
    const coupons = Object.values(entry.coupons || {});
    if (!coupons.length) return;

    const normalizedCoupons = coupons.map((coupon) => {
      const feeNumberDisplay = coupon.fee_number_display || formatCouponLabel(coupon);

      const amountValue = Number(
        coupon.third_due_amount ?? coupon.regulariza1_amount ?? coupon.amount ?? 0
      );
      const feeNumberRaw = coupon.fee_number1;
      const isAdequacy =
        Boolean(coupon.plan_regulariza) ||
        (feeNumberRaw !== null && feeNumberRaw !== undefined && Number(feeNumberRaw) === 0);

      return {
        uuid: coupon.uuid,
        fee_number_display: feeNumberDisplay,
        month: coupon.month ?? null,
        year: coupon.year ?? null,
        due_date: coupon.third_due_date || coupon.regulariza1_date || null,
        amount: amountValue,
        is_adequacy: isAdequacy,
        description: isAdequacy ? 'Adecuación' : 'Cuota regular',
      };
    });

    const totalAmount = normalizedCoupons.reduce(
      (acc, coupon) => acc + Number(coupon.amount || 0),
      0
    );

    const subscribers = getHeaderSubscribers(header);
    const fallbackSubscribers =
      subscribers.length > 0
        ? subscribers
        : [
            mapSubscriber(
              {
                uuid: header.subscriber_uuid ?? null,
                name: header.subscriber || 'Suscriptor',
                email: header.subscriber_email || 'no-user-email@gmail.com',
                phone: header.subscriber_phone ?? null,
                document: header.subscriber_document ?? null,
                is_primary: true,
              },
              0
            ),
          ];

    const dedupedSubscribers = [];
    const seenKeys = new Set();

    fallbackSubscribers.forEach((subscriber) => {
      if (!hasSubscriberData(subscriber)) {
        return;
      }
      let key = subscriber.uuid ? `uuid:${subscriber.uuid}` : null;
      if (!key && subscriber.email) {
        key = `email:${subscriber.email.toLowerCase()}`;
      }
      if (!key && subscriber.name) {
        key = `name:${subscriber.name.toLowerCase()}`;
      }

      if (key && seenKeys.has(key)) {
        return;
      }

      if (key) {
        seenKeys.add(key);
      }

      dedupedSubscribers.push(subscriber);
    });

    if (dedupedSubscribers.length === 0 && fallbackSubscribers.length > 0) {
      dedupedSubscribers.push(fallbackSubscribers[0]);
    }

    dedupedSubscribers.forEach((subscriber) => {
      emails_deudas.push({
        suscriptor: subscriber.name || header.subscriber || 'Suscriptor',
        email: subscriber.email || 'no-user-email@gmail.com',
        contrato_id: header.contract_number || '',
        cantidad: normalizedCoupons.length,
        importe: totalAmount,
        plan_nombre: header.plan_name || '',
        proximo_vencimiento: header.closest_due_date || null,
        coupons: normalizedCoupons,
        message,
      });
    });
  });

  return { emails_deudas, include_amounts: includeAmounts };
};

const createSummarySheet = (workbook, data, summary) => {
  const sheetData = [
    ['Detalle de Mora - Constructora Colonial'],
    ['Fecha de generación', formatDate(new Date())],
    [],
    ['Resumen'],
    ['Contratos listados', summary?.contracts_total ?? summary?.contracts_on_page ?? data.length],
    ['Suscriptores listados', summary?.subscribers_total ?? summary?.subscribers_on_page ?? data.length],
    [
      'Total de cuotas vencidas',
      summary?.total_overdue_installments_total ?? summary?.total_overdue_installments ?? 0,
    ],
    [
      'Monto total adeudado',
      summary
        ? summary.total_debt_amount_total ?? summary.total_debt_amount
        : 0,
    ],
    [],
    [
      'Contrato',
      'Suscriptores',
      'Emails',
      'Poseedor',
      'Cuotas vencidas',
      'Importe total',
    ],
  ];

  data.forEach((item) => {
    const header = item.header || {};
    sheetData.push([
      header.contract_number || '-',
      getSubscriberNameSummary(header),
      getSubscriberEmailSummary(header, ', '),
      header.is_possessor ? 'Sí' : 'No',
      header.overdue_installments ?? header.debt_installment ?? 0,
      Number(header.total || 0),
    ]);
  });

  const summarySheet = XLSX.utils.aoa_to_sheet(sheetData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumen');
};

const createCouponsSheet = (workbook, data) => {
  const rows = [
    ['Contrato', 'Suscriptores', 'Cuota', 'Vencimiento', 'Importe', 'Tipo', 'Emails'],
  ];

  data.forEach((item) => {
    const header = item.header || {};
    const coupons = item.coupons || [];

    coupons.forEach((coupon) => {
      const feeNumber = formatCouponLabel(coupon);
      const dueDate = coupon.third_due_date || coupon.regulariza1_date;
      const amount = coupon.third_due_amount ?? coupon.regulariza1_amount ?? 0;

      rows.push([
        header.contract_number || '-',
        getSubscriberNameSummary(header),
        feeNumber,
        dueDate ? formatDate(dueDate) : '-',
        Number(amount),
        coupon.plan_regulariza || toNumberOrNull(coupon.fee_number1) === 0 ? 'Adecuación' : 'Cuota',
        getSubscriberEmailSummary(header, ', '),
      ]);
    });
  });

  const couponsSheet = XLSX.utils.aoa_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, couponsSheet, 'Cupones');
};

const sanitizeSheetName = (name, fallback) => {
  const baseName = (name || fallback || 'Contrato').toString();
  const cleaned = baseName.replace(/[\[\]:\\\/\*\?]/g, '-').trim();
  return cleaned.slice(0, 31) || fallback || 'Contrato';
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const resolveMonthNumber = (coupon = {}) => {
  const monthFromCoupon = toNumberOrNull(coupon.month);
  if (monthFromCoupon) return monthFromCoupon;
  const dueDate = coupon.third_due_date || coupon.regulariza1_date;
  if (!dueDate) return null;
  const parsed = new Date(dueDate);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getMonth() + 1;
};

const resolveYearNumber = (coupon = {}) => {
  const yearFromCoupon = toNumberOrNull(coupon.year);
  if (yearFromCoupon) return yearFromCoupon;
  const dueDate = coupon.third_due_date || coupon.regulariza1_date;
  if (!dueDate) return null;
  const parsed = new Date(dueDate);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getFullYear();
};

const buildAdequacyLabel = (coupon = {}) => {
  const monthNumber = resolveMonthNumber(coupon);
  if (!monthNumber) return 'Adecuación';
  const monthName = ADEQUACY_MONTHS[monthNumber] || MONTH_NAMES[monthNumber];
  const yearNumber = resolveYearNumber(coupon);
  if (!monthName) return yearNumber ? `Adecuación ${yearNumber}` : 'Adecuación';
  return yearNumber ? `Adecuación ${monthName} ${yearNumber}` : `Adecuación ${monthName}`;
};

export const formatCouponLabel = (coupon = {}) => {
  const feeNumber1 = toNumberOrNull(coupon.fee_number1);
  const feeNumber2 = toNumberOrNull(coupon.fee_number2);

  if (feeNumber1 === 0 || coupon.plan_regulariza) {
    return buildAdequacyLabel(coupon);
  }

  if (feeNumber1 && feeNumber2 && feeNumber2 !== feeNumber1) {
    return `Cuotas ${feeNumber1}-${feeNumber2}`;
  }

  if (feeNumber1) {
    return `Cuota ${feeNumber1}`;
  }

  const month = resolveMonthNumber(coupon);
  const year = toNumberOrNull(coupon.year);
  if (month && year) {
    return `Cuota ${String(month).padStart(2, '0')}/${year}`;
  }

  if (coupon.month_year_display) {
    return coupon.month_year_display;
  }

  if (coupon.fee_number_display) {
    return coupon.fee_number_display;
  }

  return 'Cuota';
};

const createContractSheets = (workbook, data = []) => {
  const source = Array.isArray(data) ? data : [];
  const usedSheetNames = new Map();

  source.forEach((item, index) => {
    const header = item?.header || {};
    const coupons = item?.coupons || [];

    const detailsRows = [
      ['Contrato', header.contract_number || '-'],
      ['Suscriptores', getSubscriberCompactSummary(header)],
      ['Emails', getSubscriberEmailSummary(header, ', ')],
      ['Teléfonos', getSubscriberPhoneSummary(header, ', ')],
      ['Poseedor', header.is_possessor ? 'Sí' : 'No'],
      ['Cuotas vencidas', header.overdue_installments ?? coupons.length ?? 0],
      ['Monto adeudado', Number(header.total || 0)],
      ['Plan', header.plan_name || '-'],
      ['Próximo vencimiento', header.closest_due_date ? formatDate(header.closest_due_date) : '-'],
      ['Días hasta el vencimiento', header.days_until_due ?? '-'],
      [],
      ['Cuota', 'Vencimiento', 'Importe', 'Tipo', 'Descripción'],
    ];

    coupons.forEach((coupon) => {
      const feeNumber = formatCouponLabel(coupon);
      const dueDate = coupon.third_due_date || coupon.regulariza1_date;
      const amount = coupon.third_due_amount ?? coupon.regulariza1_amount ?? coupon.amount ?? 0;

      detailsRows.push([
        feeNumber,
        dueDate ? formatDate(dueDate) : '-',
        Number(amount),
        coupon.plan_regulariza || toNumberOrNull(coupon.fee_number1) === 0 ? 'Adecuación' : 'Cuota',
        coupon.description || '-',
      ]);
    });

    const contractSheet = XLSX.utils.aoa_to_sheet(detailsRows);
    const desiredName = header.contract_number
      ? String(header.contract_number)
      : getSubscriberNameSummary(header)
    const fallbackName = `Contrato ${index + 1}`;
    let sheetName = sanitizeSheetName(desiredName, fallbackName);

    if (usedSheetNames.has(sheetName)) {
      let counter = usedSheetNames.get(sheetName) + 1;
      let candidate = sheetName;
      do {
        candidate = sanitizeSheetName(`${sheetName}-${counter}`, fallbackName);
        counter += 1;
      } while (usedSheetNames.has(candidate));
      usedSheetNames.set(sheetName, counter - 1);
      sheetName = candidate;
    }
    usedSheetNames.set(sheetName, 1);
    XLSX.utils.book_append_sheet(workbook, contractSheet, sheetName);
  });
};

export const exportDebtDetailToExcel = async ({
  filters = {},
  filename = DEBT_DETAIL_EXPORT_PREFIX,
} = {}) => {
  const { data: exportData = [], summary: exportSummary } = await fetchDebtDetailBulk(filters);

  const sortedData = sortDebtDetailData(exportData);

  const workbook = XLSX.utils.book_new();
  createSummarySheet(workbook, sortedData, exportSummary);
  createCouponsSheet(workbook, sortedData);
  createContractSheets(workbook, sortedData);
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

const loadLogoImage = () =>
  new Promise((resolve, reject) => {
    if (!logoAsset) {
      resolve(null);
      return;
    }
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = logoAsset;
  });

export const exportDebtDetailToPDF = async ({
  filters = {},
  filename = DEBT_DETAIL_EXPORT_PREFIX,
} = {}) => {
  const { data: exportData = [], summary: exportSummary } = await fetchDebtDetailBulk(filters);

  const sortedData = sortDebtDetailData(exportData);

  const doc = new jsPDF('landscape');
  const pageWidth = doc.internal.pageSize.getWidth();
  let yOffset = 20;

  const logoImage = await loadLogoImage();
  if (logoImage) {
    doc.addImage(logoImage, 'PNG', DEFAULT_MARGIN_X, 10, 28, 18);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Constructora Colonial', pageWidth / 2, 18, { align: 'center' });
  doc.setFontSize(12);
  doc.text('Reporte de Detalle de Mora', pageWidth / 2, 26, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Generado: ${formatDate(new Date())}`, pageWidth - DEFAULT_MARGIN_X, 18, {
    align: 'right',
  });
  if (exportSummary) {
    doc.text(
      `Contratos: ${
        exportSummary.contracts_total ?? exportSummary.contracts_on_page ?? sortedData.length
      } | Suscriptores: ${
        exportSummary.subscribers_total ?? exportSummary.subscribers_on_page ?? sortedData.length
      } | Cuotas vencidas: ${
        exportSummary.total_overdue_installments_total ??
        exportSummary.total_overdue_installments ??
        0
      }`,
      pageWidth - DEFAULT_MARGIN_X,
      26,
      { align: 'right' }
    );
    doc.text(
      `Monto total adeudado: ${formatCurrency(
        exportSummary.total_debt_amount_total ?? exportSummary.total_debt_amount ?? 0
      )}`,
      pageWidth - DEFAULT_MARGIN_X,
      32,
      { align: 'right' }
    );
  }

  yOffset = 40;

  const ensureSpace = (lines = 1) => {
    const requiredSpace = lines * DEFAULT_LINE_HEIGHT + 10;
    const pageHeight = doc.internal.pageSize.getHeight();
    if (yOffset + requiredSpace > pageHeight - 20) {
      doc.addPage();
      yOffset = 20;
    }
  };

  sortedData.forEach((item, index) => {
    const header = item.header || {};
    const coupons = item.coupons || [];

    ensureSpace(6);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(
      `(${index + 1}) Contrato ${header.contract_number || '-'} - ${getSubscriberNameSummary(header)}`,
      DEFAULT_MARGIN_X,
      yOffset
    );
    yOffset += DEFAULT_LINE_HEIGHT;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(
      `Emails: ${getSubscriberEmailSummary(header)} | Teléfonos: ${getSubscriberPhoneSummary(header)}`,
      DEFAULT_MARGIN_X,
      yOffset
    );
    yOffset += DEFAULT_LINE_HEIGHT;

    doc.text(
      `Poseedor: ${header.is_possessor ? 'Sí' : 'No'} | Cuotas vencidas: ${
        header.overdue_installments ?? coupons.length
      } | Monto adeudado: ${formatCurrency(header.total ?? 0)}`,
      DEFAULT_MARGIN_X,
      yOffset
    );
    yOffset += DEFAULT_LINE_HEIGHT;

    doc.text(
      `Próximo vencimiento: ${
        header.closest_due_date ? formatDate(header.closest_due_date) : '-'
      } | Días hasta el vencimiento: ${header.days_until_due ?? '-'}`,
      DEFAULT_MARGIN_X,
      yOffset
    );
    yOffset += DEFAULT_LINE_HEIGHT;

    doc.setFont('helvetica', 'bold');
    doc.text('Detalle de cupones pendientes', DEFAULT_MARGIN_X, yOffset);
    yOffset += DEFAULT_LINE_HEIGHT;

    doc.setFontSize(8.5);
    doc.text('Cuota', DEFAULT_MARGIN_X, yOffset);
    doc.text('Vencimiento', DEFAULT_MARGIN_X + 60, yOffset);
    doc.text('Importe', DEFAULT_MARGIN_X + 110, yOffset);
    doc.text('Tipo', DEFAULT_MARGIN_X + 150, yOffset);
    yOffset += DEFAULT_LINE_HEIGHT;

    doc.setFont('helvetica', 'normal');
    coupons.forEach((coupon) => {
      ensureSpace(2);
      const feeNumber = formatCouponLabel(coupon);

      const dueDate = coupon.third_due_date || coupon.regulariza1_date;
      const amount = coupon.third_due_amount ?? coupon.regulariza1_amount ?? 0;
      const feeNumberRaw = coupon.fee_number1;
      const feeNumberValue =
        feeNumberRaw === null || feeNumberRaw === undefined ? null : Number(feeNumberRaw);
      const isAdequacy =
        coupon.plan_regulariza || (feeNumberValue !== null && !Number.isNaN(feeNumberValue) && feeNumberValue === 0);

      doc.text(feeNumber, DEFAULT_MARGIN_X, yOffset);
      doc.text(dueDate ? formatDate(dueDate) : '-', DEFAULT_MARGIN_X + 60, yOffset);
      doc.text(formatCurrency(amount), DEFAULT_MARGIN_X + 110, yOffset);
      doc.text(isAdequacy ? 'Adecuación' : 'Cuota', DEFAULT_MARGIN_X + 150, yOffset);
      yOffset += DEFAULT_LINE_HEIGHT;
    });

    yOffset += DEFAULT_LINE_HEIGHT;
  });

  doc.save(`${filename}.pdf`);
};
